import { locale, t } from './i18n';

/**
 * German for every enum this console prints.
 *
 * The backend's wire values are snake_case English identifiers, and until now most of them
 * reached the screen unchanged: a badge reading `tenant_owner`, a legal-basis card reading
 * `gdpr_de` / `opt_in` in monospace, a statistics table listing `necessary` and `desktop`.
 * That is a developer reading their own database over someone's shoulder, and it is worst
 * exactly where it matters most — the person who has to defend this configuration to a
 * supervisory authority cannot check a value they cannot read.
 *
 * One file rather than a map per screen, because the same key shows up in four places and
 * had four different treatments.
 */

/**
 * Falls back to the raw key: a new value from the server must stay visible, not vanish.
 *
 * The German label is translated here, at call time, and never where the tables are
 * written. A `t()` at module scope runs once when the module is imported and then holds
 * whatever language was current at that moment — every one of these tables would have
 * stayed German for the rest of the session.
 */
const lookup = (table: Record<string, string>, key: string | undefined): string =>
  key && table[key] ? t(table[key]!) : key || '—';

const ROLE: Record<string, string> = {
  tenant_owner: 'Inhaber',
  tenant_admin: 'Administrator',
  editor: 'Redakteur',
  analyst: 'Auswertung',
};

/** What the role may do, in one line. Shown as the hint under a role picker. */
const ROLE_HINT_DE: Record<string, string> = {
  tenant_owner: 'Darf alles, auch Team und Abrechnung.',
  tenant_admin: 'Darf Websites und Konfiguration, aber keine Inhaber ernennen.',
  editor: 'Darf bearbeiten, aber nicht veröffentlichen.',
  analyst: 'Darf lesen und auswerten, nichts ändern.',
};

const REGIME: Record<string, string> = {
  gdpr_de: 'DSGVO + TDDDG',
  gdpr_eu: 'DSGVO',
  ch_dsg: 'revDSG (Schweiz)',
  ccpa: 'CCPA/CPRA',
  none: 'Kein Regime',
};

/** The long form, for a tooltip — the short one has to fit on a row. */
const REGIME_LONG_DE: Record<string, string> = {
  gdpr_de: 'Deutschland: DSGVO und § 25 TDDDG',
  gdpr_eu: 'EU/EWR: DSGVO und ePrivacy',
  ch_dsg: 'Schweiz: revidiertes Datenschutzgesetz',
  ccpa: 'Kalifornien: CCPA/CPRA',
  none: 'Kein bekanntes Einwilligungsrecht',
};

const MODE: Record<string, string> = {
  opt_in: 'Einwilligung vorab',
  opt_out: 'Widerspruch möglich',
  notice_only: 'Nur Hinweis',
};

const CATEGORY: Record<string, string> = {
  necessary: 'Notwendig',
  functional: 'Funktional',
  analytics: 'Statistik',
  marketing: 'Marketing',
};

const DEVICE: Record<string, string> = {
  desktop: 'Desktop',
  mobile: 'Mobil',
  tablet: 'Tablet',
  bot: 'Bot',
  unknown: 'Unbekannt',
};

/**
 * Why this regime applies.
 *
 * Previously printed as the bare identifier inside a <code> tag, which made the most
 * reassuring fact in the product ("we assumed the stricter case") read like a debug trace.
 */
const REASON: Record<string, string> = {
  country_de: 'Besucher aus Deutschland',
  country_eu_eea: 'Besucher aus der EU / dem EWR',
  country_ch: 'Besucher aus der Schweiz',
  us_state_law: 'US-Bundesstaat mit eigenem Datenschutzgesetz',
  us_no_state_law: 'USA, Bundesstaat ohne eigenes Gesetz',
  rest_of_world: 'Land ohne Einwilligungspflicht',
  unknown_country_fallback: 'Land unbekannt — dann gilt das strengste Regime',
};

/** Where a vendor entry came from. The wire value is `scanner`, checked against the API. */
const SOURCE: Record<string, string> = {
  scanner: 'aus Scan',
  manual: 'von Hand',
  platform: 'Vorgabe',
  seed: 'Vorgabe',
};

/** Functions rather than the tables themselves, for the module-scope reason above. */
export const roleHint = (k?: string): string | undefined =>
  k && ROLE_HINT_DE[k] ? t(ROLE_HINT_DE[k]!) : undefined;
export const regimeLong = (k?: string): string | undefined =>
  k && REGIME_LONG_DE[k] ? t(REGIME_LONG_DE[k]!) : undefined;

export const roleLabel = (k?: string): string => lookup(ROLE, k);
export const regimeLabel = (k?: string): string => lookup(REGIME, k);
export const modeLabel = (k?: string): string => lookup(MODE, k);
export const categoryLabel = (k?: string): string => lookup(CATEGORY, k);
export const deviceLabel = (k?: string): string => lookup(DEVICE, k);
export const reasonLabel = (k?: string): string => lookup(REASON, k);
export const sourceLabel = (k?: string): string => lookup(SOURCE, k);

/**
 * Country and language names come from the platform, not from a table here.
 *
 * There was a hand-maintained list of fifteen German country names, which in an English
 * console would have needed fifteen translations of words Intl already knows in every
 * locale — and still said "Unbekannt" for the sixteenth country.
 */
export const countryLabel = (k?: string): string => {
  if (!k || k === '–' || k === '-') return t('Unbekannt');
  try {
    return new Intl.DisplayNames([locale()], { type: 'region' }).of(k) ?? k;
  } catch {
    // An invalid region code throws rather than returning undefined.
    return k;
  }
};

/**
 * Language names for the codes the editor works in.
 *
 * "Übersetzung fehlt für: en" names a field in a database. Intl.DisplayNames is the
 * platform's own table, so nothing here has to be maintained as languages are added.
 */
export const langLabel = (code: string): string => {
  try {
    return new Intl.DisplayNames([locale()], { type: 'language' }).of(code) ?? code.toUpperCase();
  } catch {
    // An invalid tag throws rather than returning undefined.
    return code.toUpperCase();
  }
};
